/**
 * CategoryPicker — single-row selector for time entry categories.
 * SDD 7.3 -- Categories fetched from API when available, falls back to shared defaults.
 * FR-PUNCH-02: Category selection before clock-in.
 */

import { useState, useEffect } from 'react';
import { CATEGORIES } from '@shared/categories';
import type { CategoryId } from '@shared/categories';
import { apiFetch } from '../lib/api';
import {
  Flame,
  ClipboardList,
  BookOpen,
  Megaphone,
  Tag,
  Heart,
  Star,
  Shield,
  Truck,
  Wrench,
  HelpCircle,
} from 'lucide-react';

interface CategoryPickerProps {
  selected: CategoryId | null;
  onChange: (category: CategoryId) => void;
}

interface CategoryOption {
  id: string;
  label: string;
  abbrev: string;
  icon: string;
}

const DEFAULT_ABBREV: Record<string, string> = {
  operational: 'OPS',
  administrative: 'ADMIN',
  training: 'TRAINING',
  public_event: 'PUB-ED',
};

const ICON_MAP: Record<string, typeof Flame> = {
  flame: Flame,
  'clipboard-list': ClipboardList,
  'book-open': BookOpen,
  megaphone: Megaphone,
  tag: Tag,
  heart: Heart,
  star: Star,
  shield: Shield,
  truck: Truck,
  wrench: Wrench,
};

const DEFAULT_ICONS: Record<string, string> = {
  operational: 'flame',
  administrative: 'clipboard-list',
  training: 'book-open',
  public_event: 'megaphone',
};

export default function CategoryPicker({ selected, onChange }: CategoryPickerProps) {
  const [options, setOptions] = useState<CategoryOption[]>(
    CATEGORIES.map((c) => ({
      id: c.id,
      label: c.label,
      abbrev: DEFAULT_ABBREV[c.id] ?? c.label.toUpperCase().slice(0, 6),
      icon: DEFAULT_ICONS[c.id] ?? 'tag',
    })),
  );

  useEffect(() => {
    let cancelled = false;
    apiFetch<{ data: { code: string; name: string; icon: string; active: number }[] }>('/api/admin/categories')
      .then((res) => {
        if (!cancelled && res.data && res.data.length > 0) {
          setOptions(
            res.data
              .filter((c) => c.active)
              .map((c) => ({
                id: c.code,
                label: c.name,
                abbrev: DEFAULT_ABBREV[c.code] ?? c.name.toUpperCase().slice(0, 6),
                icon: c.icon || 'tag',
              })),
          );
        }
      })
      .catch(() => {
        /* Fall back to static defaults */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Separate OPS (primary) from secondary categories
  const opsOption = options.find((c) => c.id === 'operational');
  const secondaryOptions = options.filter((c) => c.id !== 'operational');

  return (
    <div className="flex gap-2 w-full">
      {/* OPS — primary, larger but in the same row */}
      {opsOption &&
        (() => {
          const isSelected = selected === opsOption.id;
          const Icon = ICON_MAP[opsOption.icon] ?? HelpCircle;
          return (
            <button
              key={opsOption.id}
              type="button"
              onClick={() => onChange(opsOption.id as CategoryId)}
              aria-pressed={isSelected}
              className={`
              flex items-center justify-center gap-2 flex-[2] min-w-0 px-3 py-3
              rounded-xl border-2 text-sm font-bold
              transition-all duration-150 cursor-pointer touch-manipulation
              ${
                isSelected
                  ? 'border-action-primary bg-action-primary/10 text-action-primary'
                  : 'border-edge-default bg-surface-raised text-content-secondary hover:border-edge-strong hover:text-content-primary'
              }
            `}
            >
              <Icon className="h-5 w-5 shrink-0" />
              <span className="tracking-wide">{opsOption.abbrev}</span>
            </button>
          );
        })()}

      {/* Secondary categories — smaller, same row, equal size */}
      {secondaryOptions.map((cat) => {
        const isSelected = selected === cat.id;
        const Icon = ICON_MAP[cat.icon] ?? HelpCircle;
        return (
          <button
            key={cat.id}
            type="button"
            onClick={() => onChange(cat.id as CategoryId)}
            aria-pressed={isSelected}
            className={`
              flex flex-col items-center justify-center gap-0.5 flex-1 min-w-0 px-1 py-2
              rounded-lg border-2 text-[9px] font-bold
              transition-all duration-150 cursor-pointer touch-manipulation
              ${
                isSelected
                  ? 'border-action-primary bg-action-primary/10 text-action-primary'
                  : 'border-edge-default bg-surface-raised text-content-secondary hover:border-edge-strong hover:text-content-primary'
              }
            `}
          >
            <Icon className="h-3.5 w-3.5 shrink-0" />
            <span className="tracking-wide whitespace-nowrap">{cat.abbrev}</span>
          </button>
        );
      })}
    </div>
  );
}
